import { createPageUrl } from '@/utils';

/**
 * Where an admin lands when they open the app with nowhere particular to go.
 *
 * Admins live in the inquiry queue — every other admin surface is somewhere they
 * navigate TO after reading a customer's message. Landing them on the customer
 * Home cost a click on every session and buried the one page carrying a
 * time-sensitive backlog behind one that carries none.
 *
 * "Nowhere particular" is load-bearing: this applies to completing a sign-in and
 * to opening the app at its root, and nothing else. A deep link, a saved
 * `auth_redirect`, a "back to Home" button, the logo — all still go exactly
 * where they say. Hijacking those would make the app feel broken to the person
 * who asked for this.
 *
 * Lives here rather than in Layout.jsx so the auth callback can import one
 * string without pulling the whole nav component into its bundle.
 */
export const ADMIN_LANDING_URL = '/staff/inquiries';

/**
 * Marks that this browsing session already performed the open-the-app redirect.
 * sessionStorage, not localStorage: "first visit" means first visit of a
 * session, and an admin who navigates back to "/" later has asked to be there.
 */
export const ADMIN_LANDED_KEY = 'defera.admin_landed';

/**
 * Post-sign-in destination for a user. Non-admins are unaffected.
 *
 * Accepts either a raw Supabase session user or the mapped `User` object, since
 * the callers have different ones in hand. `app_metadata.roles` is the only
 * trusted role source (see the security note in api/entities.js) and is
 * preferred whenever present; the mapped `role` field is the fallback.
 *
 * Being wrong here is a navigation miss, not a privilege escalation: the
 * inquiries desk renders its own "This page is restricted" card for non-admins
 * and the server routes are requireAdmin-gated regardless of how you arrive.
 */
export function landingUrlFor(user) {
  const raw = user?.app_metadata?.roles;
  // When app_metadata carries roles it is the ONLY word on the subject: a
  // cached or self-set `user.role` must never promote someone it contradicts.
  // `user.role` is consulted only as a fallback when app_metadata says nothing.
  const hasAppRoles = Array.isArray(raw);
  const roles = hasAppRoles ? raw : [];
  const isAdmin = hasAppRoles ? roles.includes('admin') : user?.role === 'admin';
  // The concierge role exists to work the inquiry queue and nothing else, so
  // that queue is its home. Without this it landed on Home — with no nav entry
  // to the desk anywhere in the app, leaving it holding access it could not
  // reach (2026-09-30). Access was never the problem; the route was.
  // Never from `user.role` — the desk role is granted in app_metadata only.
  const isConcierge = hasAppRoles && roles.includes('concierge');
  return (isAdmin || isConcierge) ? ADMIN_LANDING_URL : createPageUrl('Home');
}
